import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import { ArrowUpRight, Clock, Check } from "lucide-react";

export default function Footer() {
    const { user } = useAuth();
    const [listingStatus, setListingStatus] = useState(null);

    // If the logged-in founder already has an application/listing, swap the CTA for a status message.
    useEffect(() => {
        if (user?.role !== "startup") { setListingStatus(null); return; }
        api.get("/startups/mine")
            .then((r) => setListingStatus(r.data?.status || null))
            .catch(() => setListingStatus(null));
    }, [user]);

    // Consumers (role=user) should NOT see the "Apply to list" CTA — it's only for startup founders / logged-out visitors / admins.
    const showFoundersCta = !user || user.role !== "user";
    const hasListing = listingStatus === "pending" || listingStatus === "approved";

    return (
        <footer className="border-t border-white/10 bg-black relative">
            {showFoundersCta && (
                <section data-testid="for-startups-cta" className="max-w-[1400px] mx-auto px-6 md:px-10 py-24 md:py-32 relative overflow-hidden">
                    <div className="absolute -top-20 -right-20 w-80 h-80 rounded-full bg-brand-saffron/20 blur-3xl pointer-events-none" />
                    <div className="absolute -bottom-24 -left-24 w-96 h-96 rounded-full bg-brand-magenta/20 blur-3xl pointer-events-none" />
                    <p className="font-mono-tag text-xs uppercase text-brand-saffron mb-4">/ for fashion founders</p>
                    <h2 className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl leading-[0.95] tracking-tighter mb-6 max-w-3xl uppercase">
                        {listingStatus === "approved" ? (
                            <>You're on the list.<br /><span className="text-brand-saffron">Keep dropping.</span></>
                        ) : listingStatus === "pending" ? (
                            <>Application in.<br /><span className="text-brand-saffron">Review pending.</span></>
                        ) : (
                            <>Not every brand gets in.<br /><span className="text-brand-saffron">Apply to list.</span></>
                        )}
                    </h2>
                    <p className="text-white/70 text-base md:text-lg max-w-xl mb-8">
                        We keep the store on your site. Primates is a curated discovery layer — every brand is reviewed by our editorial team before going live. If your label has the goods, apply below.
                    </p>
                    {hasListing ? (
                        <div data-testid="footer-listing-status" className="flex flex-col sm:flex-row sm:items-center gap-4">
                            <span className={`inline-flex items-center gap-2 px-4 py-3 rounded-md font-mono-tag text-xs uppercase font-bold w-fit ${
                                listingStatus === "approved" ? "bg-brand-saffron text-black" : "bg-brand-marigold/15 text-brand-marigold border border-brand-marigold/40"
                            }`}>
                                {listingStatus === "approved"
                                    ? <><Check className="w-4 h-4" /> You are already listed</>
                                    : <><Clock className="w-4 h-4" /> Your application is under review</>}
                            </span>
                            <Link to="/dashboard" className="font-mono-tag text-xs uppercase text-white/70 hover:text-brand-saffron transition-colors flex items-center gap-1">
                                Go to dashboard <ArrowUpRight className="w-3 h-3" />
                            </Link>
                        </div>
                    ) : (
                        <Link to={user?.role === "startup" ? "/dashboard" : "/register?as=startup"}>
                            <Button data-testid="footer-list-startup-btn" size="lg" className="bg-brand-saffron text-black hover:bg-brand-saffron/90 font-semibold text-base px-8 py-6 rounded-md">
                                Apply to list <ArrowUpRight className="ml-2 w-4 h-4" />
                            </Button>
                        </Link>
                    )}
                </section>
            )}

            <div className="border-t border-white/10">
                <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
                    <div>
                        <p className="font-display font-extrabold text-2xl tracking-tighter">PRIMATES<span className="text-brand-saffron">.</span></p>
                        <p className="text-white/50 text-sm mt-1">From the world, for India. India's biggest fashion startups platform.</p>
                    </div>
                    <div className="flex gap-6 font-mono-tag text-xs uppercase text-white/60 flex-wrap">
                        <Link to="/" className="hover:text-brand-saffron">Home</Link>
                        <Link to="/discover" className="hover:text-brand-saffron">Discover</Link>
                        <Link to="/drops" className="hover:text-brand-saffron">Drops</Link>
                        {showFoundersCta && (
                            <Link to="/register?as=startup" data-testid="footer-for-brands-link" className="hover:text-brand-saffron">For Brands</Link>
                        )}
                        <Link to="/privacy" data-testid="footer-privacy-link" className="hover:text-brand-saffron">Privacy</Link>
                        <Link to="/terms" data-testid="footer-terms-link" className="hover:text-brand-saffron">Terms</Link>
                    </div>
                    <p className="text-white/40 text-xs font-mono-tag">© 2026 PRIMATES</p>
                </div>
            </div>
        </footer>
    );
}
